/**
 * 用户端会员/支付相关共享类型。
 * 字段命名对齐后端响应（经 ResponseInterceptor + client 双重 camelCase 转换）。
 */

/** 等级功能对比矩阵（GET /membership/levels/compare） */
export interface CompareLevel {
  code: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  sortOrder: number;
}

export interface CompareFeature {
  key: string;
  name: string;
  groupName: string | null;
  description: string | null;
  /** 各等级对该功能的开关；按 levelCode 查找 */
  access: Array<{ levelCode: string; allowed: boolean }>;
}

export interface CompareData {
  levels: CompareLevel[];
  features: CompareFeature[];
}

/**
 * 会员套餐（GET /payment/plans），字段与后端 PlanDefinition 逐一对应。
 * 时长只有 durationMonths 一个键名，套餐名称只有 name 一个键名
 * （与管理端 GET /admin/membership/plans 同源同名）。
 */
export interface PlanItem {
  name: string;
  price: number;
  planCode: string;
  levelCode: string;
  billingCycle: 'month' | 'quarter' | 'year';
  durationMonths: number;
  renewPrice: number | null;
  autoRenewSupported: boolean;
}

/**
 * 支付渠道类型，值域与后端 PaymentRegistry 注册的 provider 键逐字一致。
 * mock 是全局支付模式而不是可配置的渠道，因此不在其中。
 */
export type PaymentChannelType = 'alipay' | 'wechat' | 'bank_transfer';

/** 支付渠道（GET /payment/channels，用户端脱敏视图） */
export interface ChannelItem {
  id: string;
  channelType: PaymentChannelType;
  channelName: string;
  isActive: boolean;
  sortOrder?: number;
  remark?: string | null;
}

/** 升级订单（GET /payment/orders）。时长键名与套餐侧统一为 durationMonths。 */
export interface OrderItem {
  id: string;
  orderNo?: string | null;
  durationMonths: number;
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
  paymentChannel: string | null;
  /** 付款方式显示名（后端按 payment_channel 回查渠道名下发；历史渠道已删时为 null） */
  paymentChannelName?: string | null;
  status: string;
  couponCode: string | null;
  promotionCode: string | null;
  paidAt: string | null;
  createdAt: string;
}

/** 创建升级订单结果（POST /payment/upgrade 的 data） */
export interface UpgradeOrder {
  id: string;
  orderNo?: string | null;
  originalAmount?: number | null;
  discountAmount?: number | null;
  finalAmount?: number | null;
  status?: string;
}

/** 银行转账收款信息（bank_transfer 模式） */
export interface BankInfo {
  bankName?: string;
  accountName?: string;
  accountNo?: string;
  branch?: string;
  qrCodeUrl?: string;
  instructions?: string;
}

/**
 * 发起支付结果（POST /payment/create-pay 的 data）。
 * 后端为判别联合：redirect=跳转链接（支付宝）、qrcode=扫码（微信）、bank_transfer=转账、mock=模拟。
 */
export type CreatePayResult =
  | { mode: 'redirect'; payUrl: string; orderId: string; outTradeNo?: string }
  | { mode: 'qrcode'; codeUrl: string; orderId: string; outTradeNo?: string }
  | { mode: 'bank_transfer'; bankInfo: BankInfo; orderId: string; outTradeNo?: string }
  | { mode: 'mock'; orderId: string; outTradeNo?: string };

/** 计费周期展示映射 */
export const BILLING_CYCLE_LABEL: Record<string, string> = {
  month: '按月',
  quarter: '按季度',
  year: '按年',
};

/** 我的邀请码与累计统计（GET /referral/my-code） */
export interface MyReferralCode {
  code: string;
  invitedCount: number;
  rewardedCount: number;
  earnedMonths: number;
  enabled: boolean;
}

/** 我的邀请记录条目（GET /referral/records，不暴露被邀请人身份） */
export interface ReferralRecordItem {
  id: string;
  status: 'pending' | 'qualified' | 'rewarded';
  rewardMonths: number;
  reason: string | null;
  createdAt: string;
  updatedAt: string;
}

/** 邀请记录状态 → 中文标签与配色 */
export const REFERRAL_STATUS_LABEL: Record<string, { text: string; cls: string }> = {
  pending: { text: '待付费', cls: 'bg-amber-100 text-amber-700' },
  qualified: { text: '已达标', cls: 'bg-blue-100 text-blue-700' },
  rewarded: { text: '已计酬', cls: 'bg-green-100 text-green-700' },
};

/** 不计酬原因 → 中文说明 */
export const REFERRAL_REASON_LABEL: Record<string, string> = {
  below_min_amount: '未达最低实付额',
  monthly_cap: '已达月度计酬上限',
};
